import { Link } from "@tanstack/react-router";

import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { cn } from "@/lib/classNames";

function HubCard({
  href,
  eyebrow,
  title,
  body,
}: {
  href: string;
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <Link
      to={href}
      className={cn(
        "group flex flex-col rounded-[28px] border-[3px] border-neutral-950 p-6 shadow-[10px_10px_0_rgba(19,19,19,0.14)] transition-transform hover:-translate-y-0.5 sm:p-8",
        MONOLITH_BOARD_STYLE.panelClassName,
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-600">{eyebrow}</p>
      <h2 className="mt-3 text-2xl font-bold uppercase tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
        {title}
      </h2>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-neutral-800">{body}</p>
      <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-950 underline decoration-2 underline-offset-4 group-hover:text-neutral-700">
        Open section →
      </p>
    </Link>
  );
}

export default function DesignSystemPage() {
  return (
    <div className="mx-auto max-w-4xl px-5 py-12 pb-24 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-10">
        <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-600">Internal review</p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif] sm:text-5xl">
          Design system
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-neutral-800">
          Palette and gradients live under Colors. Buttons, notifications, and pointers to tile and overlay
          tooling live under Components.
        </p>
      </header>
      <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <HubCard
          href="/admin/design-system/explore"
          eyebrow="Directions"
          title="Explore"
          body="Five distinct design directions for quest and journal UI, including two Notion-adjacent concepts and a fantasy-headline lorebook variant."
        />
        <HubCard
          href="/admin/design-system/colors"
          eyebrow="Tokens"
          title="Colors"
          body="Shell wash, flat UI tokens, campaign gradients, hero overlays, and the full button recipe matrix from code."
        />
        <HubCard
          href="/admin/design-system/components"
          eyebrow="Primitives"
          title="Components"
          body="Button variants, toast notifications, and links to admin surfaces for tiles and overlays."
        />
        <HubCard
          href="/admin/design-system/fonts"
          eyebrow="Map labels"
          title="Fonts"
          body="The final two-font system: Fondamento for world-map labels and Classic for readable UI copy."
        />
        <HubCard
          href="/admin/design-system/saved"
          eyebrow="Archive"
          title="Saved for later"
          body="Prototyped UI treatments preserved for future use: notice board panel and other exploratory patterns."
        />
        <HubCard
          href="/admin/design-system/sections"
          eyebrow="Patterns"
          title="Sections"
          body="Five treatments for repeating section blocks with title labels, content, and optional actions. Stacked, inset, ruled, plaque, and field card."
        />
      </div>
    </div>
  );
}
